import React from 'react'
import { FilterDescriptor } from '@progress/kendo-data-query'
import { FieldSettings, TextFilter, TextFilterProps } from '@progress/kendo-react-data-tools'
import { AccountGroupFilterKey } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { WalletCurrencyType } from 'api/Trading-Accounts/Types/WalletCurrencyType'
import { useAppSelector } from 'store'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'

import { BooleanFilter } from './BooleanFilter'
import { CompaniesFilter, CountryFilter, PlatformFilter, ServerFilter } from './common.filter'
import { SelectFilter } from './SelectFilter'

const TradingAccountTypeFilter: React.FC<TextFilterProps> = ({ filter, onFilterChange }) => {
  const tradingAccountType = useAppSelector((state) => state.tradingAccounts.tradingAccountType)

  const data = tradingAccountType.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const TradingAccountClassificationType: React.FC<TextFilterProps> = ({
  filter,
  onFilterChange,
}) => {
  const classificationTypes = useAppSelector(
    (state) => state.tradingAccounts.tradingAccountClassificationType
  )
  const data = classificationTypes.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <SelectFilter
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

const TradingAccountGroupTypeFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const tradingAccountGroupType = useAppSelector(
    (state) => state.tradingAccounts.tradingAccountGroupType
  )
  const data = tradingAccountGroupType.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const BookTypesFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const bookType = useAppSelector((state) => state.tradingAccounts.bookType)

  const data = bookType.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

const WalletCurrency = [
  { id: WalletCurrencyType.USD, label: WalletCurrencyType.USD, value: WalletCurrencyType.USD },
  { id: WalletCurrencyType.EUR, label: WalletCurrencyType.EUR, value: WalletCurrencyType.EUR },
  { id: WalletCurrencyType.GBP, label: WalletCurrencyType.GBP, value: WalletCurrencyType.GBP },
  { id: WalletCurrencyType.PLN, label: WalletCurrencyType.PLN, value: WalletCurrencyType.PLN },
  { id: WalletCurrencyType.CHF, label: WalletCurrencyType.CHF, value: WalletCurrencyType.CHF },
  { id: WalletCurrencyType.ZAR, label: WalletCurrencyType.ZAR, value: WalletCurrencyType.ZAR },
  { id: WalletCurrencyType.SGD, label: WalletCurrencyType.SGD, value: WalletCurrencyType.SGD },
  { id: WalletCurrencyType.HKD, label: WalletCurrencyType.HKD, value: WalletCurrencyType.HKD },
]

const WalletCurrencyFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={WalletCurrency} />
}

const getAccountGroupFilterFields = (): FieldSettings[] => [
  {
    name: AccountGroupFilterKey.NAME,
    label: 'Name',
    filter: TextFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.startsWithOperator,
        operator: GridFilterOperatorEnum.startsWithOperator,
      },
      {
        text: GridFilterOperatorNameEnum.likeOperator,
        operator: GridFilterOperatorEnum.likeOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notLikeOperator,
        operator: GridFilterOperatorEnum.notLikeOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.CURRENCY,
    label: 'Currency',
    filter: WalletCurrencyFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.SERVERS,
    label: 'Server Type',
    filter: ServerFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.COUNTRY,
    label: 'Country',
    filter: CountryFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.ACCOUNT_GROUP_TYPE,
    label: 'Account Group Type',
    filter: TradingAccountGroupTypeFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.CLASSIFICATION_TYPE,
    label: 'Classification Type',
    filter: TradingAccountClassificationType,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.TRADING_ACCOUNT_TYPE,
    label: 'Account Type',
    filter: TradingAccountTypeFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.BOOK_TYPE,
    label: 'Book Type',
    filter: BookTypesFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.TICKMILL_COMPANY,
    label: 'Company',
    filter: CompaniesFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.PLATFORM,
    label: 'Platform Type',
    filter: PlatformFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.IS_ENABLED,
    label: 'Is Enabled',
    filter: BooleanFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: AccountGroupFilterKey.IS_SWAP_FREE,
    label: 'IS SWAP Free',
    filter: BooleanFilter,
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
]

export { getAccountGroupFilterFields }
